import React, { CSSProperties } from "react";

import { RenderElementProps } from "slate-react/dist/components/editable";

import EmailDynamicVariable from "@mapmycustomers/shared/types/email/EmailDynamicVariable";
import { EntityTypesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";

import ElementType from "../../../../type/slate/ElementType";
import EmailDynamicVariableView from "../../../EmailDynamicVariableView";
import { cssAlignMap } from "../../type/AlignElementType";
import ResourceCallback from "../../type/ResourceCallback";
import ResourceGetter from "../../type/ResourceGetter";
import ResourceMetadataGetter from "../../type/ResourceMetadataGetter";

import Image from "./Image";
import Link from "./Link";
import ResourceLink from "./ResourceLink";

interface EditorElementProps extends RenderElementProps {
  dynamicVariables?: EmailDynamicVariable[];
  entityType?: EntityTypesSupportedByEmailFeature;
  onResourceDownload?: ResourceCallback;
  onResourceLinkRemoved?: () => void;
  readOnly?: boolean;
  resourceGetter?: ResourceGetter;
  resourceMetadataGetter?: ResourceMetadataGetter;
}

const EditorElement = ({
  attributes,
  children,
  dynamicVariables,
  element,
  entityType,
  onResourceDownload,
  onResourceLinkRemoved,
  readOnly,
  resourceGetter,
  resourceMetadataGetter,
}: EditorElementProps) => {
  const style = {
    ...(element.align ? { textAlign: cssAlignMap[element.align]! } : {}),
    ...(element.indent ? { marginLeft: `${element.indent}rem` } : {}),
  } as CSSProperties;

  switch (element.type) {
    case ElementType.LINK:
      return (
        <Link attributes={attributes} element={element} readOnly={readOnly}>
          {children}
        </Link>
      );
    case ElementType.IMAGE:
      return (
        <Image attributes={attributes} element={element} readOnly={readOnly}>
          {children}
        </Image>
      );
    case ElementType.BULLETED_LIST:
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case ElementType.LIST_ITEM:
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case ElementType.NUMBERED_LIST:
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    case ElementType.DYNAMIC_VARIABLE: {
      const dynamicVariable =
        entityType &&
        dynamicVariables?.find(
          (dynamicVariable) =>
            dynamicVariable.prefix === element.prefix && dynamicVariable.name === element.name
        );

      return (
        <span {...attributes} contentEditable={false}>
          {dynamicVariable && (
            <EmailDynamicVariableView dynamicVariable={dynamicVariable} entityType={entityType} />
          )}
        </span>
      );
    }
    case ElementType.RESOURCE_LINK:
      return (
        <ResourceLink
          attributes={attributes}
          element={element}
          onDownload={() => onResourceDownload?.(element.resourceId)}
          onRemoved={onResourceLinkRemoved}
          readOnly={readOnly}
          resource={resourceGetter?.(element.resourceId)}
          resourceMetadata={resourceMetadataGetter?.(element.resourceId)}
        >
          {children}
        </ResourceLink>
      );
  }

  return (
    <p style={style} {...attributes}>
      {children}
    </p>
  );
};

export default EditorElement;
